import type React from 'react';
import Icon, { type IconName } from '~/components2/Icon';
import InfoTooltip from '~/components2/InfoTooltip';

interface MetricCardProps {
  title: string;
  value: number | string;
  timeRangeLabel?: string;
  iconName?: IconName;
  totalValue?: number;
  tooltip?: React.ReactNode;
  'data-testid'?: string;
}

export function MetricCard({
  title,
  // if an undefined value is passed, default to 0
  value = 0,
  totalValue,
  timeRangeLabel,
  iconName,
  tooltip,
  'data-testid': dataTestId,
}: MetricCardProps) {
  return (
    <div className="h-full w-full rounded-sm border border-primary-light" data-testid={dataTestId}>
      <div className="flex h-full flex-col px-3 py-2">
        <div className="flex flex-row items-center space-x-1">
          {iconName && <Icon icon={iconName} className="rounded-md bg-gray-200 p-0.5" />}
          <div className="flex items-center gap-1">
            <div
              className="text-md break-words text-gray-800"
              data-testid={dataTestId ? `${dataTestId}-title` : undefined}
            >
              {title}
            </div>
            {tooltip && <InfoTooltip text={tooltip} label={`About ${title}`} />}
          </div>
        </div>
        {timeRangeLabel && <div className="text-md break-words text-gray-500">{timeRangeLabel}</div>}

        <div className="flex items-baseline space-x-1">
          <div
            className="self-baseline overflow-hidden break-words text-3xl font-medium"
            data-testid={dataTestId ? `${dataTestId}-value` : undefined}
          >
            {value.toLocaleString()}
          </div>
          {totalValue && (
            <div className="text-base text-secondary/70" data-testid={dataTestId ? `${dataTestId}-total` : undefined}>
              / {totalValue}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
